/**
 * Turns a LevelDefinition into three.js meshes + a CollisionWorld.
 *
 * Everything here is blockout-grade on purpose. When the real venue is modelled
 * in Blender the plan is:
 *   - load a GLB into `this.scene` and hide the primitive meshes
 *   - keep `LevelDefinition.solids` (or export it from named empties) as the
 *     collision representation, because competitive predictability matters more
 *     than the collision matching the art exactly
 *
 * Surfaces come from `src/render/surfaces.ts`, which draws every texture onto a
 * canvas at load time. The one thing worth knowing here is that UVs are scaled
 * by each mesh's *world size* rather than left at 0..1 per face: without that,
 * a 30 m barn wall and a 2 m woodpile would show the same number of boards
 * instead of the same size ones, and the whole yard would read as a doll's
 * house. See `boxGeometryFor`.
 */

import * as THREE from 'three';
import { ChargeSparks } from './ChargeSparks';
import { LIGHTING, RENDER } from '../config/gameplay';
import { FIREWOOD } from '../config/firewood';
import { Firewood, firewoodSlots } from './Firewood';
import { SceneAssets } from './SceneAssets';
import { Vehicles } from './Vehicles';
import { Tents } from './Tents';
import { Muurikka } from './Muurikka';
import { DiscoRig } from './DiscoRig';
import { Openings } from './Openings';
import { Crowd } from './Crowd';
import { Plumes } from './Plumes';
import { PALETTE, type Surface } from './surfaces';
import { levelDepthOrder, type LevelDepthOrder } from './coplanar';
import { buildSaunaBra } from './SaunaBra';
import { createSign } from './Sign';
import { sitterFigure } from './People';
import { faceKimmo } from './Pose';
import { buildRestroomMess } from './Restrooms';
import { RESTROOMS } from '../config/restrooms';
import { buildHannuFigure } from './Figure';
import type { AamupalaView } from '../world/Aamupala';
import { buildFarmVehicle } from './FarmVehicle';
import type { PaneImpact } from '../world/Ikkuna';
import { buildScreenTexture } from './screens';
import { lerp, smoothStep, type Vec3 } from '../utils/math';
import { mulberry32 } from '../utils/prng';
import type { Collider, CollisionWorld } from '../world/CollisionWorld';
import type { Door } from '../world/Door';
import {
  boxCenter,
  boxSize,
  venueFootprint,
  type Box,
  type LevelDefinition,
  type MaterialKey,
  type ScreenContent,
} from '../world/Level';

/** Hannu's. Dark, and wet enough to be darker still. */
const HANNU_HAIR = 0x2a221d;


/**
 * The unit triangular prism a camping tent is scaled out of: how wide its
 * floor is and how far its ridge stands over that floor, for a
 * `CylinderGeometry` of circumradius 1 with three sides round it. Written
 * down rather than inlined because `buildCampTents` divides the tent's real
 * width and height by them, and a tent scaled by the wrong one of the two is
 * a tent with the wrong pitch.
 */


export class World {
  readonly scene = new THREE.Scene();

  /**
   * Materials, tiled geometries and the shared unit shapes. See
   * `src/render/SceneAssets.ts`; the wrappers further down are what the
   * builders in this file still call them through.
   */
  private readonly assets: SceneAssets;
  private readonly colliderDebug = new THREE.Group();
  private firewood: Firewood | null = null;
  /** Cars and motorhomes, and the template cache behind them. */
  private readonly vehicleRenderer: Vehicles;
  /** The gazebo on the apron and the field of harjateltat east of it. */
  private readonly tentRenderer: Tents;
  /** The pan on the kivetys, the cook over it, and the breakfast minigame's props. */
  private readonly muurikka: Muurikka;
  /** The lighting rig in the XLMOTO tent. */
  private readonly disco: DiscoRig;
  /** The doors, the veräjä and the window - everything that opens. */
  private readonly openings: Openings;
  /** Everybody in the venue: where they stand and what they do afterwards. */
  private readonly crowd: Crowd;
  /** The chimney plumes and the flames in the kamiina. */
  private readonly plumes: Plumes;
  /**
   * One material per kind of screen, shared by every machine showing it - and
   * kept, because the blackout has to reach into them. See `setPowerOn`.
   */
  private readonly screenMaterials = new Map<ScreenContent, THREE.MeshBasicMaterial>();
  private hannuGroup: THREE.Group | null = null;
  private hannuHead: THREE.Object3D | undefined;
  /**
   * The arm he swings, pivoted at the shoulder. Only the one: the other is
   * posed in `Figure` and holds his tuoppi. See `renderHannu`.
   */
  private hannuSwingArm: THREE.Object3D | null = null;
  /** The F4 boxes that follow a swinging leaf, and the pieces they belong to. */
  private readonly doorDebug: { collider: Collider; lines: THREE.LineSegments }[] = [];
  /**
   * The four lights, kept so the blackout can dim them. See `renderPower`.
   * Built in `buildLights`, and null nowhere after the constructor has run.
   */
  private lights: {
    hemisphere: THREE.HemisphereLight;
    ambient: THREE.AmbientLight;
    sun: THREE.DirectionalLight;
    fill: THREE.DirectionalLight;
  } | null = null;
  /** Cleared the moment the yard's cable comes out. See `setPowerOn`. */
  private powerOn = true;
  readonly chargeSparks: ChargeSparks | null;
  /** How dark the room is right now, 0..1. Only ever moved by `renderPower`. */
  private darkness = -1;
  /** The navetta's own footprint, so the blackout knows where indoors is. */
  private readonly venue: ReturnType<typeof venueFootprint>;
  /**
   * Which of two surfaces sharing a plane is the one you see. Worked out once,
   * from the level's own geometry - see `src/render/coplanar.ts`.
   */
  private readonly depthOrder: LevelDepthOrder;

  /**
   * The collision world is *given*, not built here: it belongs to
   * RunSimulation, which has to be constructible in plain node. World only ever
   * reads it, and only to draw the F4 wireframes.
   */
  constructor(
    readonly level: LevelDefinition,
    readonly collision: CollisionWorld,
    /**
     * The renderer's max anisotropy. Passed in rather than read here because
     * World must not own a renderer - it is handed a scene to fill, not a
     * canvas to draw on.
     */
    anisotropy = 1,
  ) {
    this.assets = new SceneAssets(anisotropy);
    this.vehicleRenderer = new Vehicles(this.assets);
    this.tentRenderer = new Tents(this.assets);
    this.muurikka = new Muurikka(this.assets);
    this.disco = new DiscoRig(this.assets);
    this.openings = new Openings(this.assets, level);
    this.crowd = new Crowd(this.assets, level);
    this.plumes = new Plumes(level);
    this.venue = venueFootprint(level);
    this.depthOrder = levelDepthOrder(level);
    this.scene.background = new THREE.Color(0x9fb8cc);
    this.scene.fog = new THREE.Fog(0x9fb8cc, RENDER.fogNear, RENDER.fogFar);

    this.buildLights();
    this.buildFloors();
    this.buildSolids();
    const chargeCar = level.solids.find((solid) => solid.plate === 'WINKZI');
    const cable = level.solids.find((solid) => solid.name === level.powerCable);
    this.chargeSparks = chargeCar && cable ? new ChargeSparks({
      x: (cable.box.minX + cable.box.maxX) / 2,
      y: 0.47,
      z: chargeCar.box.minZ - 0.08,
    }, chargeCar.box) : null;
    if (this.chargeSparks) this.scene.add(this.chargeSparks.root);
    this.buildDecor();
    for (const tent of this.tentRenderer.camp(this.level.campTents)) this.scene.add(tent);
    this.placeMuurikka();
    this.buildCylinders();
    for (const person of this.crowd.build()) this.scene.add(person);
    this.crowd.collectSimaGlass(this.scene);
    if (this.level.disco) this.scene.add(this.disco.build(this.level.disco));
    this.buildSigns();
    this.buildScreens();
    this.buildHoses();
    for (const opening of this.openings.build()) this.scene.add(opening);
    this.buildHannu();
    for (const plume of this.plumes.build()) this.scene.add(plume);

    this.colliderDebug.visible = false;
    this.scene.add(this.colliderDebug);
    this.buildColliderDebug();
  }

  setCollidersVisible(visible: boolean): void {
    this.colliderDebug.visible = visible;
  }

  get collidersVisible(): boolean {
    return this.colliderDebug.visible;
  }

  /**
   * Whether the venue still has power.
   *
   * `false` from the frame somebody drags the bemari's charging cable off the
   * tolppa, `true` again on restart. Two things follow from it: every screen in
   * the level goes dead (the monitors, the valkokangas, and the charge lights
   * on the tolppa and the flap - all the same mains), and the inside of the
   * juhlanavetta goes dark. Both are pure rendering; see `LIGHTING`.
   */
  setPowerOn(on: boolean): void {
    if (on === this.powerOn) return;
    this.powerOn = on;
    // Through `material()` rather than the cache, so this is right even if it
    // is called before anything with a screen on it has been built.
    const screen = this.material('screen');
    if (screen instanceof THREE.MeshBasicMaterial) {
      screen.color.setHex(on ? PALETTE.screen : LIGHTING.deadScreen);
    }
    // The pictures on them go the same way, and by the same amount: a mapped
    // basic material multiplies its map by its colour, so white is the picture
    // as drawn and `deadScreen` is that picture with the backlight off - which
    // is not black, because a dead panel still has a room reflected in it.
    for (const material of this.screenMaterials.values()) {
      material.color.setHex(on ? 0xffffff : LIGHTING.deadScreen);
    }
    // And the tent, which is on the same tolppa as everything else in the yard.
    // The whole rig at once rather than head by head: it is one crate of lights
    // off one extension lead, and a blackout that left the lasers running would
    // be a blackout that only reached the buildings. The loop stops with it -
    // see `Game`, which is where the mixer is told.
    this.disco.setPowerOn(on);
    this.crowd.setPowerOn(on);
  }

  /**
   * Render-rate update: fades the blackout in as the camera goes indoors.
   *
   * The lights are global and there are no shadow maps, so "dark inside, light
   * outside" cannot come from the light rig - it comes from where the eye is.
   * A signed distance into the navetta's own footprint drives it, which means
   * the transition happens across the doorway in every direction, including
   * the one nobody will ever walk in from.
   */
  renderPower(eye: Vec3): void {
    if (!this.lights) return;
    const wanted = this.powerOn ? 0 : this.indoorFraction(eye.x, eye.z);
    if (wanted === this.darkness) return;
    this.darkness = wanted;

    const { lit, blackout } = LIGHTING;
    this.lights.hemisphere.intensity = lerp(lit.hemisphere, blackout.hemisphere, wanted);
    this.lights.ambient.intensity = lerp(lit.ambient, blackout.ambient, wanted);
    this.lights.sun.intensity = lerp(lit.sun, blackout.sun, wanted);
    this.lights.fill.intensity = lerp(lit.fill, blackout.fill, wanted);
  }

  /**
   * 0 out in the yard, 1 well inside the juhlanavetta, and a smooth band across
   * its walls. The distance is to the *nearest* wall, so standing in a doorway
   * gives the same answer whichever way you are facing.
   */
  private indoorFraction(x: number, z: number): number {
    const v = this.venue;
    const inside = Math.min(x - v.minX, v.maxX - x, z - v.minZ, v.maxZ - z);
    return smoothStep((inside + LIGHTING.threshold) / (LIGHTING.threshold + LIGHTING.fade));
  }

  /**
   * Render-rate update: purely visual, and not one angle of its own.
   *
   * `Door.angle` is the simulation's own answer to where the leaf is pointed -
   * ease and all - and the boxes a runner is pushed out of are cut from that
   * same number. So the leaf you can see and the leaf that shoves you cannot
   * come apart, which matters a great deal more now that they are the same
   * thing: see `src/world/Door.ts`.
   */
  renderDoors(doors: readonly Door[]): void {
    this.openings.renderDoors(doors);
  }

  /** Render-rate update for the veräjä. See `src/render/Openings.ts`. */
  renderVeraja(progress: number): void {
    this.openings.renderVeraja(progress);
  }

  /** Render-rate update for the pirtti's window and its shards. */
  renderIkkuna(brokenFor: number | null, impact: PaneImpact): void {
    this.openings.renderIkkuna(brokenFor, impact);
    if (!this.colliderDebug.visible) return;
    for (const { collider, lines } of this.doorDebug) {
      const size = boxSize(collider.box);
      const center = boxCenter(collider.box);
      lines.scale.set(size.x, size.y, size.z);
      lines.position.set(center.x, center.y, center.z);
    }
  }

  /* ------------------------------------------------------------ builders -- */

  /* --- thin wrappers over the shared asset store, kept so that the builders
     in this file read the same as the ones now living in src/render/ --- */

  private material(key: MaterialKey, depthLevel = 0): THREE.Material {
    return this.assets.material(key, depthLevel);
  }

  private get boxGeometry(): THREE.BufferGeometry {
    return this.assets.boxGeometry;
  }

  private get planeGeometry(): THREE.BufferGeometry {
    return this.assets.planeGeometry;
  }

  private buildLights(): void {
    // Low, cold morning sun. No shadow maps - PS2-era blockout, and shadows are
    // one more thing that could differ between machines.
    const hemisphere = new THREE.HemisphereLight(0xbfd8f0, 0x6a6250, LIGHTING.lit.hemisphere);
    this.scene.add(hemisphere);

    // Interiors are roofed, so without a flat ambient term the pirtti and the LAN
    // room are unreadable. Cheap and consistent beats physically plausible here.
    const ambient = new THREE.AmbientLight(0xffffff, LIGHTING.lit.ambient);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight(0xffe6c0, LIGHTING.lit.sun);
    sun.position.set(-60, 45, 30);
    this.scene.add(sun);

    const fill = new THREE.DirectionalLight(0x8fa8c8, LIGHTING.lit.fill);
    fill.position.set(80, 30, -60);
    this.scene.add(fill);

    this.lights = { hemisphere, ambient, sun, fill };
  }

  private addBox(box: Box, key: MaterialKey, name: string, depthLevel = 0): THREE.Mesh {
    const mesh = this.assets.box(box, key, name, depthLevel);
    this.scene.add(mesh);
    return mesh;
  }

  private buildFloors(): void {
    this.level.floors.forEach((spec, at) => {
      // The plane is laid flat before `rotationY`, so its u/v are world X/Z.
      const surface = this.assets.surface(spec.material);
      const geometry = surface
        ? this.tiledPlane(spec.size[0], spec.size[1], spec.material, surface)
        : this.planeGeometry;
      const mesh = new THREE.Mesh(geometry, this.material(spec.material, this.depthOrder.floors[at]));
      mesh.rotation.x = -Math.PI / 2;
      mesh.rotation.z = spec.rotationY ?? 0;
      mesh.scale.set(spec.size[0], spec.size[1], 1);
      mesh.position.set(spec.center[0], spec.y, spec.center[1]);
      mesh.name = spec.name;
      this.scene.add(mesh);
    });
  }

  private tiledPlane(
    width: number,
    depth: number,
    key: MaterialKey,
    surface: Surface,
  ): THREE.BufferGeometry {
    return this.assets.tiledPlane(width, depth, key, surface);
  }

  private buildSolids(): void {
    this.level.solids.forEach((spec, at) => {
      if (FIREWOOD.piles.includes(spec.name)) {
        this.scene.add(this.firewoodRenderer().build(firewoodSlots(spec.box), spec.name));
        return;
      }
      switch (spec.shape) {
        case 'tractor':
        case 'trailer':
        case 'drawbar':
          this.scene.add(buildFarmVehicle(spec));
          break;
        case 'car':
          this.scene.add(this.vehicleRenderer.build(spec, false));
          break;
        case 'camper':
          this.scene.add(this.vehicleRenderer.build(spec, true));
          break;
        case 'cable':
          this.scene.add(this.vehicleRenderer.cable(spec));
          break;
        case 'gate':
          // Drawn by buildVeraja, hinged so it can swing. Two copies of a gate
          // is one shut gate standing in an open one.
          break;
        case 'tent':
          this.scene.add(this.tentRenderer.add(spec));
          break;
        case 'invisible':
          // Collision only: what stands here is decor. See SolidShape.
          break;
        default:
          this.addBox(spec.box, spec.material, spec.name, this.depthOrder.solids[at]);
      }
    });
  }

  /* ----------------------------------------------------------- vehicles -- */

  /* ------------------------------------------------------------- teltta -- */

  /**
   * `boxGeometryFor`'s trick for a geometry that is not a box: scale whatever
   * UVs it came with so one tile of the material covers `Surface.worldSize`
   * metres. `spanU`/`spanV` are how much world the 0..1 range covers.
   */
  private buildDecor(): void {
    const rack = this.level.solids.find(spec => spec.name === 'klapiteline');
    if (rack) {
      this.scene.add(this.firewoodRenderer().build(firewoodSlots(rack.box), 'klapiteline-klapit'));
    }
    this.level.decor.forEach((spec, at) => {
      if (/^klapi-\d+$/.test(spec.name)) return;
      this.addBox(spec.box, spec.material, spec.name, this.depthOrder.decor[at]);
    });
    if (this.level.sauna) this.scene.add(buildSaunaBra(this.level.sauna.box));
    this.scene.add(buildRestroomMess(this.level));
    const toilet = this.level.decor.find((item) => item.name === RESTROOMS.sleeper.toilet);
    const lid = this.level.decor.find((item) => item.name === RESTROOMS.sleeper.lid);
    if (toilet && lid) {
      const figure = sitterFigure(this.assets, {
        name: 'wc-sammunut',
        seat: [(toilet.box.minX + toilet.box.maxX) / 2, (toilet.box.minZ + toilet.box.maxZ) / 2],
        seatHeight: lid.box.maxY,
        height: RESTROOMS.sleeper.height,
        rotationY: Math.PI / 2,
      }, RESTROOMS.sleeper.seed, true);
      figure.name = 'wc-sammunut';
      figure.position.set((toilet.box.minX + toilet.box.maxX) / 2, 0,
        (toilet.box.minZ + toilet.box.maxZ) / 2);
      figure.rotation.y = Math.PI / 2;
      this.scene.add(figure);
    }
  }

  /**
   * The pan and the man over it. Both go into the scene here, and the cook is
   * registered as a watching stander like every other man on his feet - he is
   * built from his own pan rather than from `level.standers`, which is the
   * only thing that sets him apart. See `src/render/Muurikka.ts`.
   */
  private placeMuurikka(): void {
    const spec = this.level.muurikka;
    if (!spec) return;
    const { pan, cook } = this.muurikka.build(spec);
    this.scene.add(pan);
    this.scene.add(cook);
    this.crowd.watchCook(cook);
  }

  /** Render-rate update for the rig in the tent. See `src/render/DiscoRig.ts`. */
  renderDisco(renderTime: number): void {
    this.disco.render(renderTime);
  }

  private firewoodRenderer(): Firewood {
    this.firewood ??= new Firewood(this.assets.surface('wood')?.map.anisotropy ?? 1);
    return this.firewood;
  }

  /**
   * What the drink looks like out in the world: whether the glass is still on
   * the stack, and whether the floor is still full of people.
   *
   * Called from `Game.render()` every frame with what the simulation says, or
   * null on a level with nobody standing in the way. Read-only, like everything
   * else render() touches.
   *
   * The glass goes when it is taken and stays gone: it is in Kimmo's hand from
   * then on, and the corner of the screen is where it is drawn from that point
   * - see `TuoppiViewmodel`, which is also where the pour is watched. A glass
   * left standing on the pile while a copy of it rode in shot would be the one
   * thing in this level that is in two places at once.
   */
  setSima(view: { taken: boolean; poured: boolean } | null): void {
    this.crowd.setSima(view);
  }

  /**
   * Whether Kimmo is still in his bed. True on the READY screen, false from
   * the frame the run starts - he is up, so the bed is empty.
   */
  setSleepingPlayerVisible(visible: boolean): void {
    this.crowd.setSleepingPlayerVisible(visible);
  }

  /** The hand at the sauna door. See `src/render/Crowd.ts`. */
  renderGreeting(age: number | null): void {
    this.crowd.renderGreeting(age);
  }

  /** The crowd in front of the rig, and the DJ behind it. */
  renderCrowd(renderTime: number, playerPosition?: Readonly<Vec3>): void {
    this.crowd.renderCrowd(renderTime, playerPosition);
  }

  /** Render-rate update for the chimney plumes. See `src/render/Plumes.ts`. */
  renderSmoke(renderTime: number): void {
    this.plumes.renderSmoke(renderTime);
  }

  /** Render-rate update for the flames in the kamiina. */
  renderFire(renderTime: number, fedFor: number | null): void {
    this.plumes.renderFire(renderTime, fedFor);
  }

  /** The awake standers turn to follow Kimmo; the cook's spatula follows his fist. */
  renderStanders(eye: Readonly<Vec3>): void {
    this.crowd.renderStanders(eye);
    this.muurikka.followGrip();
  }

  /**
   * What the aamupala minigame looks like out in the world: the pan being
   * worked, and the man in the doorway.
   *
   * Called from `Game.render()` every frame with whatever the simulation says
   * the pan is doing, or null on a level with no muurikka. Read-only, like
   * everything else render() touches - the stir is driven off `view.progress`,
   * which is a step count divided by a step count, so two machines playing one
   * replay stir the same pan at the same moment.
   */
  setAamupala(view: AamupalaView | null): void {
    this.crowd.setAamupalaDone(view?.phase === 'done');
    this.muurikka.setAamupala(view);
  }

  /** Flat colours: at this size clothing reads by shape, not by weave. */
  private figureMaterial(color: number): THREE.Material {
    return this.assets.figureMaterial(color);
  }

  private buildCylinders(): void {
    for (const spec of this.level.cylinders) {
      const geometry = new THREE.CylinderGeometry(
        spec.topRadius ?? spec.radius,
        spec.radius,
        spec.height,
        spec.radialSegments ?? 16,
        1,
      );
      const mesh = new THREE.Mesh(geometry, this.cylinderMaterial(spec.material, spec));
      mesh.position.set(spec.center[0], spec.y + spec.height / 2, spec.center[1]);
      mesh.name = spec.name;
      this.scene.add(mesh);
    }
  }

  private cylinderMaterial(
    key: MaterialKey,
    spec: { radius: number; height: number },
  ): THREE.Material {
    return this.assets.cylinderMaterial(key, spec);
  }

  /* --------------------------------------------------------------- valot -- */

  /**
   * Hannu: a man who has been in the palju long enough that the skin has come
   * off. The body is `buildHannuFigure`'s; what is his here is the colour,
   * which is the palette's and has been since he was a capsule.
   */
  private buildHannu(): void {
    const group = buildHannuFigure({
      skin: this.material('hannu'),
      skinHex: PALETTE.hannu,
      hair: this.figureMaterial(HANNU_HAIR),
      hairHex: HANNU_HAIR,
      // The three surfaces every other glass in the house is drawn in, so the
      // one in his hand is the same glass as the empties on the rim beside him.
      beer: this.material('beer'),
      foam: this.material('porcelain'),
      glass: this.material('glass'),
      rng: mulberry32(0x4a22a0),
    });
    this.hannuSwingArm = group.getObjectByName('hannu-kasi-oikea') ?? null;
    this.hannuGroup = group;
    this.hannuHead = group.getObjectByName('paa');
    this.scene.add(group);
  }

  /**
   * Positions the Hannu mesh. Called from the render loop, visuals only.
   *
   * He is never hidden. He used to be switched off below `rise = 0.01`, which
   * is the same thing as saying the tub is empty until he comes out of it -
   * and it meant the end of every sink was a man vanishing rather than a man
   * settling back down. `HANNU.submergedDepth` sits him with the water across
   * his shoulders, so there is always something in the palju to look at.
   */
  renderHannu(state: {
    position: { x: number; y: number; z: number };
    yaw: number;
    rise: number;
    phase: string;
  }, renderTime: number, eye?: Readonly<Vec3>): void {
    const group = this.hannuGroup;
    if (!group) return;
    group.position.set(state.position.x, state.position.y, state.position.z);
    group.rotation.y = state.yaw;
    if (state.rise >= 1 && eye) {
      faceKimmo(group, this.hannuHead, eye);
      // Hannu's body is built facing -Z to match the simulation's player yaw.
      group.rotation.y += Math.PI;
    } else this.hannuHead?.rotation.set(0, 0, 0);
    // A flailing arm while he is after you - one of them, and always the same
    // one. The other has a full tuoppi in it and is posed for good in
    // `Figure`; a man swinging both arms is a man who has put his beer down,
    // and Hannu has been holding that one since before you were awake.
    const swing = state.phase === 'chasing' ? Math.sin(renderTime * 11) * 0.9 : 0.15;
    if (this.hannuSwingArm) this.hannuSwingArm.rotation.x = -1.1 + swing;
  }

  private buildSigns(): void {
    for (const spec of this.level.signs) {
      this.scene.add(createSign(spec));
    }
  }

  /**
   * The pictures on the monitors.
   *
   * Unlit planes, exactly like the valkokangas and for the same reason: a
   * screen that takes the morning's light on it is a poster of a screen. They
   * are `FrontSide`, so the six machines whose backs are turned to you as you
   * come through the door show you their backs.
   */
  private buildScreens(): void {
    for (const spec of this.level.screens) {
      let material = this.screenMaterials.get(spec.content);
      if (!material) {
        material = new THREE.MeshBasicMaterial({
          map: buildScreenTexture(spec.content),
          side: THREE.FrontSide,
        });
        this.screenMaterials.set(spec.content, material);
      }
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(spec.width, spec.height), material);
      mesh.position.set(spec.center.x, spec.center.y, spec.center.z);
      mesh.rotation.y = spec.rotationY;
      mesh.name = spec.name;
      this.scene.add(mesh);
    }
  }

  /**
   * The beer lines, run as tubes through the points the level gives.
   *
   * A Catmull-Rom rather than straight segments: a hose that turns a corner in
   * one vertex is a pipe, and these are 8 mm of rubber lying over the end of a
   * bar. Six sides round, which at 18 mm is more than the eye gets back.
   */
  private buildHoses(): void {
    for (const spec of this.level.hoses) {
      const curve = new THREE.CatmullRomCurve3(
        spec.points.map((point) => new THREE.Vector3(point.x, point.y, point.z)),
      );
      const mesh = new THREE.Mesh(
        new THREE.TubeGeometry(curve, 32, spec.radius, 6, false),
        this.material(spec.material),
      );
      mesh.name = spec.name;
      this.scene.add(mesh);
    }
  }

  private buildColliderDebug(): void {
    const material = new THREE.LineBasicMaterial({ color: 0x00ff88 });
    const edges = new THREE.EdgesGeometry(this.boxGeometry);
    for (const collider of this.collision.all) {
      const size = boxSize(collider.box);
      const center = boxCenter(collider.box);
      const lines = new THREE.LineSegments(edges, material);
      lines.scale.set(size.x, size.y, size.z);
      lines.position.set(center.x, center.y, center.z);
      this.colliderDebug.add(lines);
      // The pieces of a door's leaf are the one thing in this overlay that
      // moves, and an F4 box drawn where the door was shut would be the tool
      // lying about the only collider worth watching it for. Kept in a list
      // and re-placed every frame - see `renderDoors`.
      if (collider.tag === 'door') this.doorDebug.push({ collider, lines });
    }

    const zoneMaterial = new THREE.LineBasicMaterial({ color: 0xffd25a });
    for (const checkpoint of this.level.checkpoints) {
      if (!checkpoint.box) continue;
      this.colliderDebug.add(makeWireBox(checkpoint.box, edges, zoneMaterial));
    }
    this.colliderDebug.add(
      makeWireBox(this.level.finish.zone, edges, new THREE.LineBasicMaterial({ color: 0xff3366 })),
    );
  }
}

function makeWireBox(
  box: Box,
  edges: THREE.BufferGeometry,
  material: THREE.Material,
): THREE.LineSegments {
  const size = boxSize(box);
  const center = boxCenter(box);
  const lines = new THREE.LineSegments(edges, material);
  lines.scale.set(size.x, size.y, size.z);
  lines.position.set(center.x, center.y, center.z);
  return lines;
}
